import soundsData from '../data/sounds.json';
import { sfxrGenerate, SFXR_RATE, type SfxrParams } from './sfxr';

// Prozedurale Sounds (sfxr-Stil) per WebAudio, optionale Sprachausgabe, leiser Hintergrund-Loop.
// AudioContext entsteht erst nach der ersten Berührung/Taste; ohne Audio-Unterstützung bleibt alles stumm.

export type SfxName =
  | 'pew'
  | 'boing'
  | 'flyer'
  | 'ding'
  | 'slurp'
  | 'sirene'
  | 'plaetscher'
  | 'tadaa'
  | 'murmel'
  | 'kaching'
  | 'vip'
  | 'aufgeladen'
  | 'magen'
  | 'funk'
  | 'poof'
  | 'glocke';

export const SFX_NAMES: SfxName[] = ['pew', 'boing', 'flyer', 'ding', 'slurp', 'sirene', 'plaetscher', 'tadaa', 'murmel', 'kaching', 'vip', 'aufgeladen', 'magen', 'funk', 'poof', 'glocke'];

// Spielereignis -> jsfxr-Rezept(e) aus src/data/sounds.json
const RECIPE_OF: Record<SfxName, string[]> = {
  pew: ['kuli_wurf'],
  flyer: ['flyer_wurf'],
  boing: ['treffer_boing'],
  ding: ['kunde_eingesammelt'],
  vip: ['vip_eingesammelt'],
  kaching: ['kasse_kaching'],
  slurp: ['kaffee_schluerf'],
  sirene: ['pipialarm_sirene'],
  plaetscher: ['pipi_fail_plaetschern', 'pipi_fail_troete'],
  tadaa: ['geschenk_auspacken'],
  murmel: ['nervensaege_bla'],
  aufgeladen: ['stand_aufgeladen'],
  magen: ['hungeralarm_magen'],
  funk: ['security_funk'],
  poof: ['demo_boxen_poof'],
  glocke: ['schlussglocke'],
};
const RECIPES = (soundsData as { sfx: Record<string, SfxrParams> }).sfx;
const bufCache = new Map<string, AudioBuffer>();
function recipeBuffer(id: string): AudioBuffer | null {
  if (!ctx) return null;
  const hit = bufCache.get(id);
  if (hit) return hit;
  const r = RECIPES[id];
  if (!r) return null;
  const data = sfxrGenerate(r);
  if (!data.length) return null;
  const b = ctx.createBuffer(1, data.length, SFXR_RATE);
  b.getChannelData(0).set(data);
  bufCache.set(id, b);
  return b;
}
function playRecipe(id: string, when: number): number {
  const b = recipeBuffer(id);
  if (!b || !ctx || !sfxBus) return 0;
  const s = ctx.createBufferSource();
  s.buffer = b;
  s.connect(sfxBus);
  s.start(when);
  return b.duration;
}

type AudioPrefs = { music: boolean; sfx: boolean; voice: boolean };
const PREF_KEY = 'messe_audio_v1';
const prefs: AudioPrefs = loadPrefs();

function loadPrefs(): AudioPrefs {
  const def: AudioPrefs = { music: true, sfx: true, voice: false };
  try {
    const raw = localStorage.getItem(PREF_KEY);
    if (raw) return { ...def, ...(JSON.parse(raw) as Partial<AudioPrefs>) };
  } catch {
    /* kein Speicher */
  }
  return def;
}
function savePrefs(): void {
  try {
    localStorage.setItem(PREF_KEY, JSON.stringify(prefs));
  } catch {
    /* egal */
  }
}

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let sfxBus: GainNode | null = null;
let musicBus: GainNode | null = null;
let noiseBuf: AudioBuffer | null = null;
let unlocked = false;
const stats = { played: 0, spoken: 0, last: '' as string };

function hasAudio(): boolean {
  return typeof window !== 'undefined' && typeof (window as unknown as { AudioContext?: unknown }).AudioContext === 'function';
}

export function audioUnlock(): void {
  if (unlocked) return;
  unlocked = true;
  if (!hasAudio()) return;
  try {
    ctx = new AudioContext();
    master = ctx.createGain();
    master.gain.value = 0.8;
    master.connect(ctx.destination);
    sfxBus = ctx.createGain();
    sfxBus.gain.value = prefs.sfx ? 1 : 0;
    sfxBus.connect(master);
    musicBus = ctx.createGain();
    musicBus.gain.value = prefs.music ? 0.22 : 0;
    musicBus.connect(master);
    const len = ctx.sampleRate;
    noiseBuf = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = noiseBuf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    void ctx.resume();
    startMusic();
  } catch {
    ctx = null;
  }
}

export function installAudioUnlock(): void {
  const go = (): void => audioUnlock();
  window.addEventListener('pointerdown', go, { once: true, capture: true });
  window.addEventListener('touchstart', go, { once: true, capture: true });
  window.addEventListener('keydown', go, { once: true, capture: true });
}

// ---- Bausteine
function tone(type: OscillatorType, f0: number, f1: number, t0: number, dur: number, vol: number, bus: GainNode | null = sfxBus): void {
  if (!ctx || !bus) return;
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  o.type = type;
  o.frequency.setValueAtTime(f0, t0);
  if (f1 !== f0) o.frequency.exponentialRampToValueAtTime(Math.max(20, f1), t0 + dur);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.linearRampToValueAtTime(vol, t0 + 0.008);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  o.connect(g).connect(bus);
  o.start(t0);
  o.stop(t0 + dur + 0.02);
}
function noise(t0: number, dur: number, vol: number, filt: BiquadFilterType, f0: number, f1 = f0, q = 1): void {
  if (!ctx || !sfxBus || !noiseBuf) return;
  const s = ctx.createBufferSource();
  s.buffer = noiseBuf;
  s.loop = true;
  const bq = ctx.createBiquadFilter();
  bq.type = filt;
  bq.Q.value = q;
  bq.frequency.setValueAtTime(f0, t0);
  if (f1 !== f0) bq.frequency.exponentialRampToValueAtTime(Math.max(20, f1), t0 + dur);
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.linearRampToValueAtTime(vol, t0 + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  s.connect(bq).connect(g).connect(sfxBus);
  s.start(t0, Math.random() * 0.5);
  s.stop(t0 + dur + 0.02);
}

const lastPlay: Partial<Record<SfxName, number>> = {};
const MIN_GAP: Partial<Record<SfxName, number>> = { pew: 0.05, boing: 0.06, flyer: 0.06, murmel: 0.8, sirene: 1.5 };

export function sfx(name: SfxName): void {
  stats.last = name;
  if (!ctx || !prefs.sfx) return;
  const t = ctx.currentTime;
  const gap = MIN_GAP[name] ?? 0.03;
  if (t - (lastPlay[name] ?? -9) < gap) return;
  lastPlay[name] = t;
  stats.played++;
  const rec = RECIPE_OF[name];
  if (rec) {
    let at = t;
    let okAny = false;
    for (const id of rec) {
      const d = playRecipe(id, at);
      if (d > 0) okAny = true;
      at += d * 0.8;
    }
    if (okAny) return;
  }
  switch (name) {
    case 'pew':
      tone('square', 1300, 260, t, 0.16, 0.07);
      break;
    case 'boing':
      tone('sine', 180, 520, t, 0.09, 0.22);
      tone('sine', 520, 140, t + 0.09, 0.22, 0.18);
      break;
    case 'flyer':
      noise(t, 0.22, 0.16, 'bandpass', 2400, 5200, 0.8);
      noise(t + 0.06, 0.14, 0.08, 'highpass', 4000);
      break;
    case 'ding':
      tone('triangle', 1318, 1318, t, 0.22, 0.16);
      tone('triangle', 1760, 1760, t + 0.13, 0.32, 0.16);
      break;
    case 'slurp':
      noise(t, 0.38, 0.18, 'bandpass', 500, 1700, 6);
      tone('sine', 300, 700, t + 0.05, 0.3, 0.05);
      break;
    case 'sirene':
      for (let i = 0; i < 4; i++) {
        tone('sawtooth', 700, 1100, t + i * 0.3, 0.15, 0.06);
        tone('sawtooth', 1100, 700, t + i * 0.3 + 0.15, 0.15, 0.06);
      }
      break;
    case 'plaetscher':
      for (let i = 0; i < 9; i++) noise(t + i * 0.07 + Math.random() * 0.03, 0.09, 0.14, 'bandpass', 900 + Math.random() * 1600, 400, 4);
      tone('sawtooth', 240, 120, t + 0.55, 0.5, 0.1); // Tröte
      tone('square', 180, 90, t + 0.55, 0.5, 0.05);
      break;
    case 'tadaa':
      tone('square', 523, 523, t, 0.12, 0.07);
      tone('square', 659, 659, t + 0.1, 0.12, 0.07);
      tone('square', 784, 784, t + 0.2, 0.12, 0.07);
      tone('square', 1046, 1046, t + 0.3, 0.45, 0.08);
      break;
    case 'murmel':
      for (let i = 0; i < 5; i++) tone('triangle', 150 + Math.random() * 70, 120 + Math.random() * 60, t + i * 0.11, 0.1, 0.08);
      break;
    case 'aufgeladen':
      tone('square', 440, 880, t, 0.12, 0.06);
      tone('square', 660, 1320, t + 0.1, 0.16, 0.06);
      break;
    case 'kaching':
      noise(t, 0.08, 0.12, 'highpass', 3000);
      tone('square', 1568, 1568, t + 0.04, 0.12, 0.07);
      tone('square', 2093, 2093, t + 0.12, 0.35, 0.07);
      break;
  }
}

export function playAllSfx(): void {
  SFX_NAMES.forEach((n, i) => setTimeout(() => sfx(n), i * 900));
}

// ---- Hintergrund-Loop aus sounds.json (musik.fahrstuhl), sehr leise
type Song = { bpm: number; melodie: [string, number][]; bass: [string, number][]; welle_melodie: OscillatorType; welle_bass: OscillatorType };
const SONGS = (soundsData as unknown as { musik: Record<string, Song> }).musik;
let musicTimer: number | null = null;
let songName = 'fahrstuhl';
function noteHz(n: string): number {
  const m = /^([A-G])(#|b)?(\d)$/.exec(n);
  if (!m) return 0;
  const base: Record<string, number> = { C: -9, D: -7, E: -5, F: -4, G: -2, A: 0, B: 2 };
  const semi = base[m[1]!]! + (m[2] === '#' ? 1 : m[2] === 'b' ? -1 : 0) + (Number(m[3]) - 4) * 12;
  return 440 * 2 ** (semi / 12);
}
type Voice = { notes: [string, number][]; i: number; next: number; wave: OscillatorType; vol: number };
let voices: Voice[] = [];
function startMusic(): void {
  if (!ctx || musicTimer != null) return;
  const song = SONGS[songName] ?? Object.values(SONGS)[0];
  if (!song) return;
  const beat = 60 / song.bpm;
  const t0 = ctx.currentTime + 0.3;
  voices = [
    { notes: song.melodie, i: 0, next: t0, wave: song.welle_melodie, vol: 0.07 },
    { notes: song.bass, i: 0, next: t0, wave: song.welle_bass, vol: 0.12 },
  ];
  musicTimer = window.setInterval(() => {
    if (!ctx || !musicBus) return;
    for (const v of voices) {
      while (v.next < ctx.currentTime + 0.5) {
        const [n, len] = v.notes[v.i % v.notes.length]!;
        v.i++;
        const dur = len * beat;
        const hz = n === 'R' ? 0 : noteHz(n);
        if (hz > 0) tone(v.wave, hz, hz, v.next, Math.max(0.12, dur * 0.9), v.vol, musicBus);
        v.next += dur;
      }
    }
  }, 150);
}
export function setSong(name: string): void {
  if (!SONGS[name] || name === songName) return;
  songName = name;
  if (musicTimer != null) {
    clearInterval(musicTimer);
    musicTimer = null;
    startMusic();
  }
}

// ---- Sprachausgabe
let lastSpeakAt = 0;
const SPEAK_GAP_MS = 3500;
export function speak(text: string, figureId: number): void {
  if (!prefs.voice || !text) return;
  try {
    const ss = window.speechSynthesis;
    if (!ss || typeof SpeechSynthesisUtterance === 'undefined') return;
    const now = performance.now();
    if (ss.speaking || ss.pending || now - lastSpeakAt < SPEAK_GAP_MS) return;
    lastSpeakAt = now;
    const u = new SpeechSynthesisUtterance(text.replace(/…/g, '.'));
    u.lang = 'de-DE';
    const h = ((figureId * 2654435761) >>> 0) / 4294967296;
    const h2 = ((figureId * 40503 + 977) % 1000) / 1000;
    u.pitch = 0.7 + h * 0.9;
    u.rate = 0.95 + h2 * 0.3;
    u.volume = 0.9;
    const v = ss.getVoices().find((x) => x.lang?.toLowerCase().startsWith('de'));
    if (v) u.voice = v;
    ss.speak(u);
    stats.spoken++;
  } catch {
    /* keine Sprachausgabe */
  }
}

// ---- Einstellungen
export function audioPrefs(): Readonly<AudioPrefs> {
  return prefs;
}
export function toggleAudio(k: keyof AudioPrefs): boolean {
  prefs[k] = !prefs[k];
  savePrefs();
  if (ctx) {
    if (k === 'sfx' && sfxBus) sfxBus.gain.value = prefs.sfx ? 1 : 0;
    if (k === 'music' && musicBus) musicBus.gain.value = prefs.music ? 0.22 : 0;
  }
  if (k === 'voice' && !prefs.voice) {
    try {
      window.speechSynthesis?.cancel();
    } catch {
      /* egal */
    }
  }
  return prefs[k];
}
export function audioInfo(): { ctx: string; prefs: AudioPrefs; played: number; spoken: number; last: string } {
  return { ctx: ctx ? ctx.state : 'none', prefs: { ...prefs }, ...stats };
}
